import { Palette, Globe, Video, Share2, Camera, PartyPopper, Monitor, PenTool, BarChart3, Users, Newspaper, type LucideIcon } from "lucide-react";

export interface LeistungItem {
  icon: LucideIcon;
  title: string;
  text: string;
}

export interface LeistungPackage {
  name: string;
  description: string;
  includes: string[];
}

export interface LeistungSection {
  id: string;
  slug: string;
  icon: LucideIcon;
  title: string;
  shortLabel: string;
  description: string;
  longDescription: string;
  items: LeistungItem[];
  packages: LeistungPackage[];
  seoTitle: string;
  seoDescription: string;
}

export const sections: LeistungSection[] = [
  {
    id: "medienproduktion",
    slug: "medienproduktion",
    icon: Video,
    title: "Medienproduktion",
    shortLabel: "Medienproduktion",
    description: "Film, Foto & alles dazwischen. Wir bringen deine Geschichte in Bild & Ton, ob in Bochum, NRW oder deutschlandweit.",
    longDescription: "Von Imagefilm über Produktfotografie bis Eventbegleitung: Wir produzieren cinematic Content, der Marken sichtbar macht und Emotionen transportiert.",
    items: [
      { icon: Video, title: "Videoproduktion", text: "Imagefilm, Werbevideos, Aftermovie, Eventdokumentation & einfacher Social-Media-Content. Inklusive Messebegleitung, Firmenevents, Geburtstagen & Feiern." },
      { icon: Camera, title: "Fotoproduktion", text: "Portraitbilder, Produktbilder, Immobilienfotografie & Ausstattungs-/Interieurfotografie. Bildsprache, die Marken & Räume stark inszeniert." },
    ],
    packages: [
      { name: "Videoproduktion", description: "Bewegte Bilder, die deine Marke & deine Events transportieren. Von Imagefilm über Werbespot bis Aftermovie alles aus einer Hand.", includes: ["Imagefilm & Markenfilm", "Werbevideos & Spots", "Aftermovie & Eventdokumentation", "Messebegleitung & Standdokumentation", "Firmenevents, Galas & Konferenzen", "Geburtstage, Feiern & private Events", "Einfacher Content für Social Media (Reels, Shorts, TikTok)", "Konzept, Storytelling & lizenzfreie Musik", "Drohnenaufnahmen, Full HD & 4K"] },
      { name: "Fotoproduktion", description: "Starke Bilder schaffen Vertrauen & verkaufen. Wir setzen Menschen, Produkte & Räume hochwertig in Szene.", includes: ["Portraitbilder & Teamfotos", "Produktbilder & Produktfotografie", "Immobilienfotografie (Innen & Außen)", "Ausstattungs- & Interieurfotografie", "Content für Web, Print & Social Media", "Moodboard & Bildsprache-Entwicklung"] },
    ],
    seoTitle: "Medienproduktion Bochum – Imagefilm, Fotografie & Eventfilm | 4Facemedia",
    seoDescription: "Cinematic Medienproduktion aus Bochum: Imagefilm, Videoproduktion, Produktfotografie & Eventfilm. Premium-Qualität, deutschlandweit im Einsatz.",
  },
  {
    id: "corporate-identity",
    slug: "corporate-identity",
    icon: Palette,
    title: "Corporate Identity",
    shortLabel: "Corporate Identity",
    description: "Dein Markenauftritt – durchdacht, konsistent und unverwechselbar.",
    longDescription: "Wir entwickeln Marken mit Wiedererkennungswert: Logo, Farbwelt, Typografie & komplette Geschäftsausstattung – aus einem Guss.",
    items: [
      { icon: PenTool, title: "Logo & Branding", text: "Wir entwickeln Logos und visuelle Identitäten, die deine Marke einzigartig machen und in Erinnerung bleiben." },
      { icon: Newspaper, title: "Geschäftsausstattung", text: "Visitenkarten, Briefpapier, Verpackungsdesign alles aus einem Guss für einen professionellen Auftritt." },
    ],
    packages: [
      { name: "Branding Basic", description: "Das starke Fundament für deinen Markenauftritt. \nFür einen klaren, professionellen Start mit allem, was du wirklich brauchst.", includes: ["Logo-Design (2 Entwürfe)", "Farbpalette & Typografie", "Logo-Dateien in allen Formaten"] },
      { name: "Business", description: "Strategisches Wachstum für Marken, die mehr wollen.\nFür einen durchdachten, konsistenten Auftritt mit wieder erkennungswert.", includes: ["Alles aus Branding Basic", "Gestaltung & Druckabwicklung von Druckprodukten", "Brand Strategie", "Brand Guidelines"] },
      { name: "Enterprise", description: "Der ganzheitliche Markenauftritt mit klarer Vision. Für Marken mit Anspruch, die Design als strategisches Werkzeug nutzen.", includes: ["Alles aus Business", "Komplette Geschäftsausstattung", "Verpackungsdesign", "Social-Media-Templates", "Brand Book"] },
    ],
    seoTitle: "Corporate Identity & Branding Bochum – Logo, Markenwelt & Design | 4Facemedia",
    seoDescription: "Corporate Identity aus Bochum: Logo-Design, Branding, Brand Guidelines & komplette Geschäftsausstattung. Marken mit Wiedererkennungswert.",
  },
  {
    id: "webseiten",
    slug: "webseiten-erstellung",
    icon: Globe,
    title: "Webseiten-Erstellung",
    shortLabel: "Webseiten",
    description: "Moderne, performante Webseiten aus Bochum, die überzeugen & konvertieren. Für Unternehmen in ganz Deutschland.",
    longDescription: "Vom Onepager bis zum Online-Shop: schnelle, responsive Webseiten mit klarem Fokus auf Nutzerführung, SEO & Conversion.",
    items: [
      { icon: Monitor, title: "Responsive Design", text: "Jede Webseite funktioniert auf jedem Gerät vom Smartphone bis zum Desktop. Kein Nachgedanke, sondern Grundlage von Anfang an." },
      { icon: Globe, title: "CMS & E-Commerce", text: "Inhalte selbst pflegen, Produkte verwalten, Bestellungen abwickeln. Wir bauen Systeme die ihr ohne Entwickler bedienen könnt." },
    ],
    packages: [
      { name: "ONEPAGER", description: "Alles auf einer Seite kompakt, übersichtlich und auf den Punkt.", includes: ["Einzelseite mit durchdachter Seitenstruktur", "Responsive Design für alle Endgeräte", "SEO-Grundoptimierung für lokale Sichtbarkeit", "Kontaktmöglichkeit und rechtssichere Pflichtseiten"] },
      { name: "Business Web", description: "Für Unternehmen die mehr zeigen wollen als eine Seite hergibt.", includes: ["Bis zu 7 Unterseiten (erweiterbar) - Content-Management-System zum selbst pflegen", "SEO-Optimierung für organische Reichweite", "Performance-Optimierung für schnelle Ladezeiten"] },
      { name: "E-Commerce", description: "Ein Online-Shop der verkauft nicht nur Produkte auflistet.", includes: ["Individuelles Shop-Design mit Fokus auf Nutzerführung", "Produktverwaltung und Zahlungsabwicklung", "Mobile Optimierung mit Conversion-Fokus", "SEO & GEO Performance", "3 Monate technischer Support nach Launch"] },
    ],
    seoTitle: "Webseiten-Erstellung Bochum – Webdesign, CMS & E-Commerce | 4Facemedia",
    seoDescription: "Webdesign aus Bochum: responsive Webseiten, CMS & Online-Shops. Performant, SEO-optimiert & conversion-stark. Für Kunden in ganz Deutschland.",
  },
  {
    id: "social-media",
    slug: "social-media-marketing",
    icon: Share2,
    title: "Social Media Marketing",
    shortLabel: "Social Media",
    description: "Strategie, Content & Community-Management für echte Reichweite. Social Media Agentur aus Bochum, bundesweit aktiv.",
    longDescription: "Datengetriebene Social-Media-Strategie, kreativer Content & professionelles Account-Management – alles aus einer Hand.",
    items: [
      { icon: BarChart3, title: "Strategie & Analyse", text: "Datengetriebene Social-Media-Strategien, die messbare Ergebnisse liefern und deine Zielgruppe erreichen." },
      { icon: Users, title: "Content & Betreuung", text: "Kreativer Content, Community & Account-Management und regelmäßige Reportings alles aus einer Hand." },
      { icon: Video, title: "Content Produktion", text: "Sie wollen Ihren Social Media Auftritt selbst steuern, aber nicht auf professionellen Content verzichten? Wir liefern Ihnen maßgeschneiderte Foto- und Videoinhalte bereit zur direkten Verwendung auf Ihren Kanälen." },
    ],
    packages: [
      { name: "Social Starter", description: "Ideal für den Einstieg in Social Media mit regelmäßigem Content.", includes: ["\nContent-Plan & Strategie", "Account- & Community-Management", "Regelmäßige Postings (Basis-Frequenz)", "Community & Account-Management", "Monatliches Reporting & Analyse"] },
      { name: "Social Growth", description: "Mehr Reichweite und Engagement durch strategisches Community & Account Management.", includes: ["Content-Plan & Strategie", "Account- & Community-Management", "Erhöhte Posting-Frequenz", "Community & Account-Management", "Monatliches Reporting & Analyse"] },
      { name: "Social Premium", description: "Skalierbares Social Media Management für maximale Reichweite, Wachstum und starke Markenpräsenz.", includes: ["Content-Strategie & Planung", "\n\nAccount- & Community-Management", "Erhöhte Posting-Frequenz", "Alle Plattformen", "Monatliches Reporting & Analyse", "Tägliches Monitoring"] },
    ],
    seoTitle: "Social Media Marketing Bochum – Strategie, Content & Betreuung | 4Facemedia",
    seoDescription: "Social Media Agentur aus Bochum: Strategie, Content-Produktion & Account-Management für Instagram, TikTok & Co. Bundesweit aktiv.",
  },
];

export const getSectionBySlug = (slug: string) => sections.find((s) => s.slug === slug);
